import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@mesh.ai/rabbit';
import { addYears, differenceInDays, parseISO, subYears } from 'date-fns';
import { isNil } from 'lodash';
import { useDispatch } from 'react-redux';
import {
  useReviewCreationV1Data,
  setReviewCreationKey,
  setReviewCreationTimelines,
  setReviewDateOffset,
} from 'reducers/reviewCreationV1Slice';
import { useGetTimezoneOffset } from 'screens/performanceReview/helpers';
import { getDateWithEndOfDay, getDateWithStartOfDay } from 'utils/helpers/dateHelpers';

import { convertDateToString6 } from 'constants/helper';

import { DateRangeSelectDashed } from 'uiToolkitV1/DateRange/DateRangeSelectDashed';
import { NumberInput } from 'uiToolkitV1/InputV2/NumberInput';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { Reminders } from '../components/Reminders';
import { ReviewCreationSidepanel } from '../components/ReviewCreationSidepanel';
import { CONTINUOUS_REVIEW_TYPES, REVIEW_CREATION_TYPES } from '../constants/reviewCreationConstants';
import {
  useInvalidateTimelineRemindersPanelData,
  useUpdateTimelineReminders,
} from '../dataHooks/timelineAndRemindersSectionHooks';

export const ALL_SECTIONS_IN_ORDER = ['NOMINATOR', 'SELF', 'MANAGER', 'PEER', 'UPWARD', 'DLM', 'RATING_CALIBRATE'];
export const REVIEW_RESPONSE_PROVIDER_TYPE_WITH_REMINDER = ['SELF', 'MANAGER', 'PEER', 'UPWARD', 'DLM'];

const REVIEW_RESPONSE_PROVIDER_TYPE_LABEL = {
  SELF: 'Self Review',
  MANAGER: 'Manager Review',
  PEER: 'Peer Review',
  UPWARD: 'Upward Review',
  NOMINATOR: 'Nomination',
  RATING_CALIBRATE: 'Calibration',
  DLM: 'Dotted Line Manager Review',
  PUBLISHING: 'Publishing',
};

export const REVIEW_RESPONSE_PROVIDER_TYPE_INFO = {
  SELF: 'The employee reviews their own performance and contributions',
  MANAGER: "The employee's reporting manager who provides feedback and typically completes the final review",
  PEER: 'Colleagues and team members who collaborate with the employee and provide lateral feedback',
  UPWARD: "Direct reports who provide feedback on their manager's leadership and performance",
  NOMINATOR: 'Process of selecting and inviting peer and dotted line reviewers to provide feedback for an employee',
  RATING_CALIBRATE:
    'Process of reviewing, adjusting, and finalizing performance results before publishing them to the employee',
  DLM: "Secondary or matrix managers (functional, project, or specialized role managers) who provide input on specific aspects of the employee's work",
};

export const validateOneTimeTimelinesDates = (timelines) => {
  const isInvalidDatePresent = Object.keys(timelines).some((stage) => {
    const { startDate, endDate } = timelines[stage];
    return !startDate || !endDate;
  });
  return !isInvalidDatePresent;
};

export const validateContunuousTimeline = (providerTimelineDetails) => {
  const isInvalidOffsetPresent = Object.keys(providerTimelineDetails).some((stage) => {
    const { startDateOffset, endDateOffset } = providerTimelineDetails[stage];
    return isNil(startDateOffset) || isNil(endDateOffset) || startDateOffset > endDateOffset;
  });
  return !isInvalidOffsetPresent;
};

const Header = () => (
  <Flex p="8px 12px 4px 2px" justifyContent="space-between" alignItems="center">
    <TextBodyMedium bold>Timeline & reminders</TextBodyMedium>
  </Flex>
);

const TimezoneNote = () => {
  const formattedOffset = useGetTimezoneOffset();

  return (
    <Box flex={1} py={2}>
      <TextLabel bold>Note on Time Zone:</TextLabel>
      <TextLabel color="text.g2" mt={2}>
        {`Note: The start and end times for reviewer submissions follow the organization's time zone ${formattedOffset}.
        Hence, all forms open at 00:00 hrs of the start date and all forms close at 23:59 hrs of the end date of that time zone.`}
      </TextLabel>
    </Box>
  );
};

const ReminderFields = ({ responseProviderType }) => {
  const dispatch = useDispatch();

  const { reminders: reminders1 } = useReviewCreationV1Data();
  const reminders = reminders1[responseProviderType] ?? [];

  const onReminderChange = useCallback(
    (newReminders) => {
      dispatch(
        setReviewCreationKey({
          reminders: {
            ...reminders1,
            [responseProviderType]: newReminders.map((reminder) => ({
              ...reminder,
              reviewStage: responseProviderType,
            })),
          },
        })
      );
    },
    [dispatch, reminders1, responseProviderType]
  );

  return (
    <Flex alignSelf="flex-end" mt={2} width="100%" pl="3.5">
      <Reminders remindersSettings={reminders ?? []} onChange={onReminderChange} />
    </Flex>
  );
};

const SingleTimelineSelection = ({ responseProviderType, displayNumber }) => {
  const { timelines } = useReviewCreationV1Data();

  const dispatch = useDispatch();
  const minDate = subYears(getDateWithStartOfDay(), 5);
  const maxDate = addYears(getDateWithEndOfDay(), 5);

  const { startDate: startDateString, endDate: endDateString } = timelines[responseProviderType];
  const startDateObj = useMemo(() => (startDateString ? new Date(parseISO(startDateString)) : null), [startDateString]);
  const endDateObj = useMemo(() => (endDateString ? new Date(parseISO(endDateString)) : null), [endDateString]);

  const onDateRangeChange = useCallback(
    ({ startDate, endDate }) => {
      dispatch(
        setReviewCreationTimelines({
          [responseProviderType]: {
            reviewStage: responseProviderType,
            startDate: convertDateToString6(startDate),
            endDate: convertDateToString6(endDate),
          },
        })
      );
    },
    [dispatch, responseProviderType]
  );

  const supportsReminderCreation = REVIEW_RESPONSE_PROVIDER_TYPE_WITH_REMINDER.includes(responseProviderType);

  const infoText = REVIEW_RESPONSE_PROVIDER_TYPE_INFO[responseProviderType] ?? '';

  return (
    <Flex flexDirection="column" p="10px 12px 0 12px" alignItems="stretch">
      <Flex justifyContent="space-between">
        <Flex width="240px" alignItems="flex-start" gap={1}>
          <TextBodyMedium color="text.g2">
            {displayNumber}. {REVIEW_RESPONSE_PROVIDER_TYPE_LABEL[responseProviderType]}
          </TextBodyMedium>
          {infoText && <TooltipInfoV1 title={infoText} />}
        </Flex>
        <Flex flexDirection="column" flexGrow={1} mt={-3}>
          <DateRangeSelectDashed
            onSelect={onDateRangeChange}
            displayTwoMonthsLayout={false}
            placeholderText="Select Duration"
            initialDateRange={{ startDate: startDateObj, endDate: endDateObj }}
            // onClear={onDateRangeClear}
            minDate={minDate}
            maxDate={maxDate}
            displayCancelButton={false}
            displayClearButton={false}
            width="100%"
            isPortalContent={false}
          />

          {startDateString && endDateString && (
            <TextLabel color="text.g2" mt={1}>
              {differenceInDays(endDateObj, startDateObj) + 1} Days total duration
            </TextLabel>
          )}
        </Flex>
      </Flex>

      {supportsReminderCreation && <ReminderFields responseProviderType={responseProviderType} />}
    </Flex>
  );
};

const getDuration = (startDateOffset, endDateOffset) => {
  if (isNil(startDateOffset) || isNil(endDateOffset)) {
    return null;
  }
  if (Number.isNaN(startDateOffset) || Number.isNaN(endDateOffset)) {
    return null;
  }
  return endDateOffset - startDateOffset;
};

const SingleStageOffsetEdit = ({ responseProviderType }) => {
  const { providerTimelineDetails, continuousReviewType } = useReviewCreationV1Data();

  const dispatch = useDispatch();

  const { startDateOffset, endDateOffset } = providerTimelineDetails[responseProviderType];

  const durationDaysCount = getDuration(startDateOffset, endDateOffset);

  const onChangePropertyHandler = (value_, propertyName) => {
    const value = Number.isNaN(value_) ? null : value_;
    if (propertyName === 'durationDaysCount') {
      dispatch(
        setReviewDateOffset({
          [responseProviderType]: [
            {
              offsetType: 'endDateOffset',
              value: value === null ? value : startDateOffset + value,
            },
          ],
        })
      );
    } else if (propertyName === 'startDateOffset') {
      // in case startoffset is changed, we need to update end date offset as well cause we are showing date duration on UI
      dispatch(
        setReviewDateOffset({
          [responseProviderType]: [
            {
              offsetType: 'startDateOffset',
              value,
            },
            { offsetType: 'endDateOffset', value: value + durationDaysCount },
          ],
        })
      );
    }
  };

  const getTypeText = () => {
    switch (continuousReviewType) {
      case CONTINUOUS_REVIEW_TYPES.WORK_ANNIVERSARY:
        return 'after work-anniversary';
      case CONTINUOUS_REVIEW_TYPES.PIP:
        return 'after date of qualifying for cycle';
      default:
        return 'after date of joining';
    }
  };

  const getTypeText2 = () => {
    switch (continuousReviewType) {
      case CONTINUOUS_REVIEW_TYPES.WORK_ANNIVERSARY:
        return 'work-anniversary is on';
      case CONTINUOUS_REVIEW_TYPES.PIP:
        return 'date of qualifying for cycle';
      default:
        return 'date of joining is';
    }
  };

  const typeText = getTypeText();
  const typeText2 = getTypeText2();

  return (
    <Flex flexDir="column" gap={1}>
      <Flex flexDirection="column" flexGrow={1} mt={-3}>
        <Flex gap={3} alignItems="center">
          <TextBodyMedium>Starts</TextBodyMedium>

          <NumberInput
            value={startDateOffset ?? ''}
            onChange={(valueAsString, valueAsNumber) => onChangePropertyHandler(valueAsNumber, 'startDateOffset')}
            textAlign="start"
            w="60px"
            type="number"
            min={0}
            max={365}
            inputMode="numeric"
            format={(val) => (Number.isNaN(val) ? '' : val)}
            // isInvalid={!(startDateOffset <= endDateOffset)}
          />
          <TextBodyMedium>days {typeText}</TextBodyMedium>
        </Flex>

        <Flex gap={3} alignItems="center">
          <TextBodyMedium>Valid for</TextBodyMedium>

          <NumberInput
            value={durationDaysCount ?? ''}
            onChange={(valueAsString, valueAsNumber) => onChangePropertyHandler(valueAsNumber, 'durationDaysCount')}
            textAlign="start"
            w="60px"
            type="number"
            min={0}
            max={365}
            inputMode="numeric"
            format={(val) => (Number.isNaN(val) ? '' : val)}
            // isInvalid={!(startDateOffset <= endDateOffset)}
          />
          <TextBodyMedium>days</TextBodyMedium>
        </Flex>

        <TextLabel color="text.g2" mt={1}>
          For example, if your {typeText2} 1st January, then the self review period runs from 7th to 14th January.
        </TextLabel>
      </Flex>
    </Flex>
  );
};

const SingleContinuousTimelineSelection = ({ responseProviderType, displayNumber }) => {
  const supportsReminderCreation = REVIEW_RESPONSE_PROVIDER_TYPE_WITH_REMINDER.includes(responseProviderType);

  return (
    <Flex flexDirection="column" p="10px 12px 0 12px" alignItems="stretch">
      <Flex justifyContent="space-between">
        <TextBodyMedium color="text.g2" width="240px">
          {displayNumber}. {REVIEW_RESPONSE_PROVIDER_TYPE_LABEL[responseProviderType]}
        </TextBodyMedium>
        <SingleStageOffsetEdit responseProviderType={responseProviderType} />
      </Flex>

      {supportsReminderCreation && <ReminderFields responseProviderType={responseProviderType} />}
    </Flex>
  );
};

const OneTimeTimelineAndReminders = () => {
  const { timelines } = useReviewCreationV1Data();

  const applicableSections = ALL_SECTIONS_IN_ORDER.filter((section) => timelines[section]);

  return (
    <Flex flexDir="column" gap={4}>
      {applicableSections.map((section, index) => (
        <SingleTimelineSelection responseProviderType={section} displayNumber={index + 1} />
      ))}
    </Flex>
  );
};

const ContinuousTimelineAndReminders = () => {
  const { providerTimelineDetails } = useReviewCreationV1Data();

  const applicableSections = ALL_SECTIONS_IN_ORDER.filter((section) => providerTimelineDetails[section]);

  return (
    <Flex flexDir="column" gap={4}>
      {applicableSections.map((section, index) => (
        <SingleContinuousTimelineSelection responseProviderType={section} displayNumber={index + 1} />
      ))}
    </Flex>
  );
};

// value is in context of related api data boolean isAllowPeerNominationBySelf
export const NOMINATOR_OPTIONS = [
  { label: 'Employee, Manager', value: true },
  { label: 'Manager', value: false },
];

const TimelinesRemindersPanel = () => {
  const dispatch = useDispatch();
  const { mutate: updateTimelineReminders } = useUpdateTimelineReminders();
  const invalidateTimelineReminders = useInvalidateTimelineRemindersPanelData();

  const { reviewType, isTimelineRemindersPanelOpen, timelines, providerTimelineDetails } = useReviewCreationV1Data();

  const handleClose = () => {
    dispatch(setReviewCreationKey({ isTimelineRemindersPanelOpen: false }));
    invalidateTimelineReminders();
  };

  const handleSaveClick = () => {
    updateTimelineReminders();
  };

  const isSaveDisabled = useMemo(
    () =>
      (reviewType === REVIEW_CREATION_TYPES.DEFAULT && !validateOneTimeTimelinesDates(timelines)) ||
      (reviewType === REVIEW_CREATION_TYPES.TRIGGERED && !validateContunuousTimeline(providerTimelineDetails)),
    [providerTimelineDetails, reviewType, timelines]
  );

  return (
    <ReviewCreationSidepanel
      isOpen={isTimelineRemindersPanelOpen}
      onClose={handleClose}
      onSave={handleSaveClick}
      isSaveDisabled={isSaveDisabled}
    >
      <Flex py={2} flexDir="column" gap={2} mb={1}>
        <Header />
      </Flex>
      <Flex borderRadius="8px" flexDir="column" border="4px solid #E7EBEF" bg="white" pb="4">
        <Flex flexDir="column" py={2} px={3} gap={6}>
          <TimezoneNote />
        </Flex>

        {reviewType === REVIEW_CREATION_TYPES.DEFAULT ? (
          <OneTimeTimelineAndReminders />
        ) : (
          <ContinuousTimelineAndReminders />
        )}
      </Flex>
    </ReviewCreationSidepanel>
  );
};

const tabbedTimelinesRemindersPanels = [
  {
    name: 'Review 1',
    // queryParamId: 'timeline',
    Comp: () => <ContinuousTimelineAndReminders />,
  },
  {
    name: 'Review 2',
    // queryParamId: 'reminders',
    Comp: () => <ContinuousTimelineAndReminders />,
  },
  {
    name: 'Review 3',
    // queryParamId: 'launch',
    Comp: () => <ContinuousTimelineAndReminders />,
  },
];

const TabbedTimelinesRemindersPanel = () => {
  const dispatch = useDispatch();
  const invalidateTimelineReminders = useInvalidateTimelineRemindersPanelData();
  const {
    reviewType,
    isTimelineRemindersPanelOpen,
    timelines,
    providerTimelineDetails,
    selectedProbation_30_60_90_index,
  } = useReviewCreationV1Data();

  const handleClose = () => {
    dispatch(setReviewCreationKey({ isTimelineRemindersPanelOpen: false }));
    invalidateTimelineReminders();
  };

  const handleSaveClick = () => {
    // do nothing to make save api call, as it's just mock data on UI
    dispatch(setReviewCreationKey({ isTimelineRemindersPanelOpen: false }));
  };

  const isSaveDisabled = useMemo(
    () =>
      (reviewType === REVIEW_CREATION_TYPES.DEFAULT && !validateOneTimeTimelinesDates(timelines)) ||
      (reviewType === REVIEW_CREATION_TYPES.TRIGGERED && !validateContunuousTimeline(providerTimelineDetails)),
    [providerTimelineDetails, reviewType, timelines]
  );

  const onTabChangeHandler = (value) => {
    dispatch(setReviewCreationKey({ selectedProbation_30_60_90_index: value }));
  };

  const dummyRef = useRef(null);

  useLayoutEffect(() => {
    setTimeout(() => {
      dummyRef?.current?.scrollIntoView();
    }, 0);
  }, [isTimelineRemindersPanelOpen]);

  return (
    <ReviewCreationSidepanel
      isOpen={isTimelineRemindersPanelOpen}
      onClose={handleClose}
      onSave={handleSaveClick}
      isSaveDisabled={isSaveDisabled}
    >
      {/* just for scrolling back to top, the Tabs somehow auto scroll which I am not able to prevent after trying all methods */}
      <Box ref={dummyRef} />
      <Flex py={2} flexDir="column" gap={2} mb={1}>
        <Header />
      </Flex>
      <Flex flexDir="column" bg="white" pb="4">
        <Flex flexDir="column" gap={6}>
          <TimezoneNote />
        </Flex>

        <Tabs
          variant="secondary"
          value={selectedProbation_30_60_90_index}
          onValueChange={onTabChangeHandler}
          className="mt-1"
        >
          <TabsList>
            {tabbedTimelinesRemindersPanels.map(({ name }, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <TabsTrigger key={index} value={index}>
                {name}
              </TabsTrigger>
            ))}
          </TabsList>

          {tabbedTimelinesRemindersPanels.map(({ Comp }, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <TabsContent key={index} value={index}>
              <Box px={3} py={6}>
                <Comp />
              </Box>
            </TabsContent>
          ))}
        </Tabs>
      </Flex>
    </ReviewCreationSidepanel>
  );
};

export { TimelinesRemindersPanel, TabbedTimelinesRemindersPanel };
